import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { groupBy, sumBy } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import Currency from 'models/enums/Currency';
import LineItem from 'models/enums/LineItem';
import LossCodeSelect from 'module/claims/components/lossCode/LossCodeSelect';
import { addJobItemRowButtonStyles } from 'module/claims/views/claimEdit/sections/claimJobEdit/claimJobEditItem/ClaimEdit.Job.Item';
import { ClaimJobProps, usePublicClaimSubmitProvider } from '../../../Claim.Submit.Hooks';
import { getLineItemLabel } from 'module/claims/helper/LineItem.Label';

export default function ClaimSubmitClaimJobItems({ claimJob, index }: ClaimJobProps) {
  const { lang } = getLocales();
  const { form, isEditingTax, setIsEditingTax, updateJobItem, deleteJobItem, addJobItem } =
    usePublicClaimSubmitProvider();

  const claimJobs = form.data?.claim?.claimJobs || [];

  return (
    <>
      {!!claimJob.claimJobItems?.length && form.data?.claimCreationOption === ClaimCreatorOptions.DEALERSHIP ? (
        <div
          key={index}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: Theme.Size.L,
            marginLeft: Theme.Size.XS,
            marginRight: Theme.Size.XS,
          }}
        >
          <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>
            {lang.jobItemTemplates} {claimJobs.length > 1 ? `#${index + 1}` : ''}
          </h2>

          {[LineItem.part, LineItem.labour, LineItem.deductible, LineItem.other]
            .filter((code) => groupBy(claimJob.claimJobItems, 'jobItemTypeCode')[code]?.length)
            .map((code) => {
              const group = groupBy(claimJob.claimJobItems, 'jobItemTypeCode')[code];
              return (
                <div key={code} style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
                    <h4>{getLineItemLabel(code)}</h4>
                  </div>
                  <table className={ThemeTableSimple} style={{ fontSize: Theme.FontSize.S }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '10px', width: '20px' }}>#</th>
                        <th style={{ textAlign: 'center' }}>{lang.payee}</th>
                        {code !== LineItem.deductible && <th style={{ textAlign: 'center' }}>{lang.lossCode}</th>}
                        {code === LineItem.part && (
                          <>
                            <th style={{ textAlign: 'center' }}>{lang.baseCost}</th>
                            <th style={{ textAlign: 'center' }}>{lang.quantity}</th>
                          </>
                        )}
                        <th style={{ textAlign: 'center' }}>{lang.requestedAmount}</th>
                        {code !== LineItem.deductible && (
                          <>
                            <th style={{ textAlign: 'center' }}>{lang.gstHst}</th>
                            <th style={{ textAlign: 'center' }}>{lang.pst}</th>
                          </>
                        )}
                        <th style={{ textAlign: 'center' }}>{lang.total}</th>
                        {claimJob?.claimJobItems?.some(
                          (it) => !it.isCreatedFromTemplate && it.jobItemTypeCode !== LineItem.deductible
                        ) && <th style={{ textAlign: 'center' }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      <DataMap
                        data={group}
                        render={({ data: item, index: itemIndex }) => (
                          <tr key={itemIndex + item.currency}>
                            <td style={{ width: '20px', padding: '10px' }}>{itemIndex + 1}</td>
                            <td style={{ width: '15%' }}>{item.claimJobItemPayee?.dealer?.name}</td>
                            {item.jobItemTypeCode !== LineItem.deductible && (
                              <td style={{ width: '140px' }}>
                                <LossCodeSelect
                                  width="140px"
                                  lossCodes={item.lossCodes}
                                  value={item?.lossCode}
                                  disabled={item.lossCodes?.length === 1}
                                  error={!item.lossCode && lang.required}
                                  onChange={({ lossCode }) => updateJobItem(index, 'lossCode', lossCode, item)}
                                />
                              </td>
                            )}
                            {item.jobItemTypeCode === LineItem.part && (
                              <>
                                <td>
                                  <NumberInput
                                    name="baseCost"
                                    value={Number(item?.baseCost?.toFixed(2))}
                                    onChange={({ baseCost }) => updateJobItem(index, 'baseCost', baseCost, item)}
                                    step={0.01}
                                    style={{ input: { textAlign: 'right' } }}
                                    iconBefore={<Icon class="fas-dollar-sign" />}
                                  />
                                </td>
                                <td>
                                  <NumberInput
                                    name="quantity"
                                    value={Number(item?.quantity)}
                                    onChange={({ quantity }) => updateJobItem(index, 'quantity', quantity, item)}
                                    step={1}
                                    style={{ input: { textAlign: 'center' } }}
                                  />
                                </td>
                              </>
                            )}
                            <td>
                              <NumberInput
                                name="correction"
                                value={Number(item?.correction?.toFixed(2) ?? 0)}
                                readOnly={
                                  item.jobItemTypeCode === LineItem.part || item.jobItemTypeCode === LineItem.deductible
                                }
                                onChange={({ correction }) => updateJobItem(index, 'correction', correction, item)}
                                step={0.01}
                                style={{ input: { textAlign: 'right' } }}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                              />
                            </td>
                            {item.jobItemTypeCode !== LineItem.deductible && (
                              <>
                                <td>
                                  <NumberInput
                                    name="gstHst"
                                    value={Number(((item?.gstHst || 0.0) * item?.adjudication)?.toFixed(2))}
                                    onChange={({ gstHst }) =>
                                      updateJobItem(index, 'gstHst', gstHst / item?.adjudication, item)
                                    }
                                    onClick={() => {
                                      setIsEditingTax(true);
                                    }}
                                    step={0.01}
                                    readOnly={!isEditingTax}
                                    htmlElementProps={{ title: !isEditingTax ? lang.clickToEdit : undefined }}
                                    error={
                                      item?.gstHst > 1 &&
                                      lang.mustBeEqualOrLessThanAmount.replace(
                                        '{amount}',
                                        `${lang.requestedAmount}: ${item?.adjudication?.toFixed(2)}`
                                      )
                                    }
                                    style={{ input: { textAlign: 'right', cursor: 'text' }, cursor: 'text' }}
                                    iconBefore={<Icon class="fas-dollar-sign" />}
                                  />
                                </td>
                                <td>
                                  <NumberInput
                                    name="provincialSalesTax"
                                    value={Number(((item?.provincialSalesTax || 0.0) * item?.adjudication)?.toFixed(2))}
                                    onChange={({ provincialSalesTax }) =>
                                      updateJobItem(
                                        index,
                                        'provincialSalesTax',
                                        provincialSalesTax / item?.adjudication,
                                        item
                                      )
                                    }
                                    onClick={() => {
                                      setIsEditingTax(true);
                                    }}
                                    step={0.01}
                                    readOnly={!isEditingTax}
                                    htmlElementProps={{ title: !isEditingTax ? lang.clickToEdit : undefined }}
                                    error={
                                      item?.provincialSalesTax > 1 &&
                                      lang.mustBeEqualOrLessThanAmount.replace(
                                        '{amount}',
                                        `${lang.requestedAmount}: ${item?.adjudication?.toFixed(2)}`
                                      )
                                    }
                                    style={{ input: { textAlign: 'right', cursor: 'text' }, cursor: 'text' }}
                                    iconBefore={<Icon class="fas-dollar-sign" />}
                                  />
                                </td>
                              </>
                            )}
                            <td style={{ width: '15%', padding: '0 12px 0 0' }}>
                              <NumberInput
                                name="total"
                                value={Number(item?.total?.toFixed(2) ?? 0)}
                                readOnly
                                onChange={({ total }) => updateJobItem(index, 'total', total, item)}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                              />
                            </td>
                            {!item.isCreatedFromTemplate && item.jobItemTypeCode !== LineItem.deductible && (
                              <td style={{ padding: Theme.Size.S }}>
                                <Button
                                  class={ThemeButtonCircle}
                                  media={<Icon class="fas-trash" />}
                                  onClick={() => {
                                    deleteJobItem(index, code, itemIndex);
                                  }}
                                  htmlElementProps={{
                                    title: lang.delete,
                                  }}
                                />
                              </td>
                            )}
                          </tr>
                        )}
                      />
                    </tbody>
                    {code !== LineItem.deductible &&
                      claimJob?.claimJobItems?.some((it) => !it.isCreatedFromTemplate) && (
                        <tfoot>
                          <tr>
                            <td colSpan={code === LineItem.part ? 10 : 8} style={{ padding: Theme.Size.S }}>
                              <Button
                                style={addJobItemRowButtonStyles}
                                class={ThemeButtonCircle}
                                media="fas-plus"
                                onClick={() => addJobItem(index, code)}
                                render={getLineItemLabel(code)}
                                htmlElementProps={{
                                  title: `${lang.addNew} ${getLineItemLabel(code)} ${lang.item}`,
                                }}
                              />
                            </td>
                          </tr>
                        </tfoot>
                      )}
                  </table>
                </div>
              );
            })}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong>
                {lang.totalAmountWithoutTaxes}:{' '}
                {formatNumberWithSeparator(sumBy(ClaimJob.aggregates(claimJob as ClaimJob), (it) => it.ttl).toFixed(2))}{' '}
                {Currency.CAD}
              </strong>
              <strong>
                {lang.totalAmountWithTaxes}:{' '}
                {formatNumberWithSeparator(
                  sumBy(ClaimJob.aggregates(claimJob as ClaimJob), (it) => it.total).toFixed(2)
                )}{' '}
                {Currency.CAD}
              </strong>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
